import { describeError, uploadToPresignedUrl, useApi } from '@classgo/app-kit';
import type { GuardianItem, InvitationView, StudentDetail } from '@classgo/shared';
import { Badge, Button, Card, EmptyState, Modal, Select, useToast } from '@classgo/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { type ChangeEvent, useState } from 'react';

import { listQuery } from '../../academic/api';
import { ConfirmDialog } from '../../components/confirm';
import { ConsentForm } from '../../forms/consent-form';
import { LinkForm } from '../../forms/link-form';
import { SensitiveForm } from '../../forms/sensitive-form';
import { StatusForm } from '../../forms/status-form';
import { StudentForm } from '../../forms/student-form';
import { useT } from '../../i18n';
import { formatDate } from '../../lib/dates';
import { peopleApi, peopleQueries } from '../../people/api';
import { recordQueries } from '../../records/api';
import { useTenant } from '../../tenant/tenant-provider';
import { StudentStatus } from '../students';
import { ReportCardsCard } from './report-cards';

const fullName = (person: { givenName: string | null; familyName: string | null }) =>
  `${person.givenName ?? ''} ${person.familyName ?? ''}`.trim();

/**
 * One student (ticket #68): identity with photo, enrollment and moves, dated status changes and
 * history, guardians with links and invitations, the sensitive section behind permission and
 * consent (with the consent capture inline), and the way to the report card.
 */
export const StudentDetailScreen = () => {
  const t = useT();
  const api = useApi();
  const toast = useToast();
  const navigate = useNavigate();
  const queries = useQueryClient();
  const { session, sede } = useTenant();
  const { studentId = '' } = useParams({ strict: false });
  const student = useQuery(recordQueries.student(api, studentId));
  const [modal, setModal] = useState<'edit' | 'status' | 'link' | 'enroll' | 'delete' | null>(null);
  const [editingLink, setEditingLink] = useState<GuardianItem | null>(null);
  const [unlinking, setUnlinking] = useState<GuardianItem | null>(null);
  const can = (key: string) => session.permissions.all || session.permissions.keys.includes(key);
  const refresh = async () => {
    await queries.invalidateQueries({ queryKey: ['school', 'students'] });
    await queries.invalidateQueries({ queryKey: ['people', 'students', studentId] });
  };

  if (student.isPending) return <p className="text-sm text-gray-500">{t('common.loading')}</p>;
  if (student.isError || !student.data) {
    return (
      <EmptyState
        title={t('entity.error.title')}
        description={student.isError ? describeError(t, student.error) : ''}
        action={
          <Link
            to="/estudiantes"
            className="text-brand-600 underline underline-offset-2 hover:decoration-2"
          >
            {t('student.back')}
          </Link>
        }
      />
    );
  }
  const data = student.data;
  const row = (label: string, value: string | null | undefined) => (
    <div>
      <dt className="text-gray-500">{label}</dt>
      <dd className="mt-0.5 font-medium text-gray-900">{value || t('common.none')}</dd>
    </div>
  );
  return (
    <div className="space-y-4">
      <Link
        to="/estudiantes"
        className="text-sm text-brand-600 underline underline-offset-2 hover:decoration-2"
      >
        ← {t('student.back')}
      </Link>
      <Card>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-start gap-4">
            <Photo student={data} canEdit={can('students:write')} onChanged={refresh} />
            <div>
              <h2 className="text-2xl font-semibold text-gray-900">{fullName(data)}</h2>
              <p className="mt-1 text-sm text-gray-500">
                {data.groupName
                  ? `${data.gradeName ?? ''} · ${data.groupName}`
                  : t('student.enroll.none')}
              </p>
              <div className="mt-2 flex items-center gap-2">
                <StudentStatus status={data.status} />
                {data.enrollment ? (
                  <span className="text-xs text-gray-500">
                    {t(
                      data.enrollment.schoolYearStatus === 'planned'
                        ? 'student.enrollment.planned'
                        : 'student.enrollment',
                      { year: data.enrollment.schoolYearName },
                    )}
                  </span>
                ) : null}
              </div>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {can('students:write') ? (
              <>
                <Button variant="secondary" onClick={() => setModal('edit')}>
                  {t('student.edit')}
                </Button>
                <Button variant="secondary" onClick={() => setModal('status')}>
                  {t('student.status.change')}
                </Button>
                <Button variant="secondary" onClick={() => setModal('enroll')}>
                  {data.enrollment ? t('student.enroll.move') : t('student.enroll.now')}
                </Button>
              </>
            ) : null}
            <Button
              onClick={() =>
                void navigate({ to: '/estudiantes/$studentId/boletin', params: { studentId } })
              }
            >
              {t('student.reportCard')}
            </Button>
          </div>
        </div>
      </Card>
      <div className="grid gap-4 lg:grid-cols-2">
        <Card title={t('student.identity')}>
          <dl className="grid gap-4 text-sm sm:grid-cols-2">
            {row(
              t('student.field.document'),
              data.documentNumber ? `${data.documentType} ${data.documentNumber}` : null,
            )}
            {row(t('student.field.birthDate'), data.birthDate ? formatDate(data.birthDate) : null)}
            {row(t('student.field.email'), data.email)}
            {data.enrollment
              ? row(
                  t('student.field.enrolledOn'),
                  data.enrollment.enrolledOn ? formatDate(data.enrollment.enrolledOn) : null,
                )
              : null}
          </dl>
          {can('users:invite') && data.email ? <StudentAccount studentId={studentId} /> : null}
        </Card>
        <Card
          title={t('student.guardians')}
          actions={
            can('guardians:write') ? (
              <Button size="sm" variant="secondary" onClick={() => setModal('link')}>
                {t('link.add')}
              </Button>
            ) : undefined
          }
        >
          {data.guardians.length === 0 ? (
            <p className="text-sm text-gray-500">{t('student.guardians.empty')}</p>
          ) : (
            <ul className="divide-y divide-gray-100 text-sm">
              {data.guardians.map((guardian) => (
                <li
                  key={guardian.id}
                  className="flex flex-wrap items-center justify-between gap-2 py-2"
                >
                  <div>
                    <p className="font-medium text-gray-900">
                      {fullName(guardian)}
                      <span className="ml-2 text-xs font-normal text-gray-500">
                        {guardian.relationship}
                      </span>
                    </p>
                    <p className="text-xs text-gray-500">{guardian.email}</p>
                    {can('users:invite') ? (
                      <InvitationStatus userId={guardian.id} email={guardian.email} />
                    ) : null}
                  </div>
                  <div className="flex flex-wrap items-center gap-1">
                    {guardian.isPrimary ? (
                      <Badge tone="brand">{t('student.guardian.primary')}</Badge>
                    ) : null}
                    {guardian.billingResponsible ? (
                      <Badge tone="info">{t('student.guardian.billing')}</Badge>
                    ) : null}
                    {can('guardians:write') ? (
                      <>
                        <Button size="sm" variant="ghost" onClick={() => setEditingLink(guardian)}>
                          {t('link.edit')}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-danger-700"
                          onClick={() => setUnlinking(guardian)}
                        >
                          {t('link.remove')}
                        </Button>
                      </>
                    ) : null}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>
        {can('students:sensitive') ? (
          <SensitiveSection studentId={studentId} guardians={data.guardians} />
        ) : null}
        {can('grades:read') ? <ReportCardsCard studentId={studentId} /> : null}
        <HistoryCard studentId={studentId} />
      </div>
      {can('students:write') ? (
        <div className="flex justify-end">
          <Button variant="ghost" className="text-danger-700" onClick={() => setModal('delete')}>
            {t('student.delete')}
          </Button>
        </div>
      ) : null}

      <Modal open={modal === 'edit'} onClose={() => setModal(null)} title={t('student.edit')}>
        {modal === 'edit' ? (
          <StudentForm
            current={data}
            onDone={() => {
              setModal(null);
              void refresh();
            }}
          />
        ) : null}
      </Modal>
      <Modal
        open={modal === 'status'}
        onClose={() => setModal(null)}
        title={t('student.status.change')}
      >
        {modal === 'status' ? (
          <StatusForm
            studentId={studentId}
            current={data.status}
            onDone={() => {
              setModal(null);
              void refresh();
            }}
          />
        ) : null}
      </Modal>
      <Modal
        open={modal === 'enroll'}
        onClose={() => setModal(null)}
        title={data.enrollment ? t('student.enroll.move') : t('student.enroll.now')}
      >
        {modal === 'enroll' ? (
          <EnrollForm
            student={data}
            sedeId={sede.id}
            onDone={() => {
              setModal(null);
              void refresh();
            }}
          />
        ) : null}
      </Modal>
      <Modal open={modal === 'link'} onClose={() => setModal(null)} title={t('link.add')}>
        {modal === 'link' ? (
          <LinkForm
            studentId={studentId}
            current={null}
            onDone={() => {
              setModal(null);
              void refresh();
            }}
          />
        ) : null}
      </Modal>
      <Modal
        open={editingLink !== null}
        onClose={() => setEditingLink(null)}
        title={t('link.edit')}
      >
        {editingLink ? (
          <LinkForm
            studentId={studentId}
            current={editingLink}
            onDone={() => {
              setEditingLink(null);
              void refresh();
            }}
          />
        ) : null}
      </Modal>
      <ConfirmDialog
        open={unlinking !== null}
        title={t('link.remove')}
        body={t('link.remove.body', { name: unlinking ? fullName(unlinking) : '' })}
        confirmLabel={t('link.remove')}
        cancelLabel={t('common.cancel')}
        onClose={() => setUnlinking(null)}
        onConfirm={() => {
          if (!unlinking) return;
          void peopleApi(api)
            .unlink(studentId, unlinking.id)
            .then(() => {
              setUnlinking(null);
              void refresh();
            })
            .catch((error: unknown) =>
              toast.show({
                tone: 'error',
                title: t('link.remove'),
                description: describeError(t, error),
              }),
            );
        }}
      />
      <ConfirmDialog
        open={modal === 'delete'}
        title={t('student.delete')}
        body={t('student.delete.body', { name: fullName(data) })}
        confirmLabel={t('student.delete')}
        cancelLabel={t('common.cancel')}
        onClose={() => setModal(null)}
        onConfirm={() => {
          void peopleApi(api)
            .deleteStudent(studentId)
            .then(async () => {
              await queries.invalidateQueries({ queryKey: ['school', 'students'] });
              await navigate({ to: '/estudiantes' });
            })
            .catch((error: unknown) =>
              toast.show({
                tone: 'error',
                title: t('student.delete'),
                description: describeError(t, error),
              }),
            );
        }}
      />
    </div>
  );
};

const Photo = ({
  student,
  canEdit,
  onChanged,
}: {
  student: StudentDetail;
  canEdit: boolean;
  onChanged: () => Promise<void>;
}) => {
  const t = useT();
  const api = useApi();
  const toast = useToast();
  const url = useQuery(peopleQueries.photoUrl(api, student.photoKey));
  const upload = useMutation({
    mutationFn: async (file: File) => {
      const presigned = await peopleApi(api).presignPhoto(
        file.name,
        file.type || 'image/jpeg',
        file.size,
      );
      await uploadToPresignedUrl({
        url: presigned.url,
        file,
        contentType: file.type || 'image/jpeg',
      });
      await peopleApi(api).updateStudent(student.id, { photoKey: presigned.key });
    },
    onSuccess: () => void onChanged(),
    onError: (error) =>
      toast.show({
        tone: 'error',
        title: t('student.photo'),
        description: describeError(t, error),
      }),
  });
  const onFile = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) upload.mutate(file);
  };
  return (
    <div className="flex flex-col items-center gap-1">
      <div className="flex h-20 w-20 items-center justify-center overflow-hidden rounded-full bg-gray-100 text-2xl text-gray-400">
        {url.data?.url ? (
          <img src={url.data.url} alt="" className="h-full w-full object-cover" />
        ) : (
          <span aria-hidden>👤</span>
        )}
      </div>
      {canEdit ? (
        <label className="control-touch inline-flex cursor-pointer items-center rounded-control px-1 text-xs text-brand-600 underline underline-offset-2 hover:decoration-2">
          {upload.isPending ? t('common.loading') : t('student.photo')}
          <input
            type="file"
            accept="image/*"
            className="sr-only"
            onChange={onFile}
            disabled={upload.isPending}
          />
        </label>
      ) : null}
    </div>
  );
};

const EnrollForm = ({
  student,
  sedeId,
  onDone,
}: {
  student: StudentDetail;
  sedeId: string;
  onDone: () => void;
}) => {
  const t = useT();
  const api = useApi();
  const years = useQuery(listQuery(api, 'years'));
  const groups = useQuery(listQuery(api, 'groups', { sedeId }));
  const activeYear = (years.data?.items ?? []).find((year) => year.status === 'active');
  const history = useQuery(peopleQueries.history(api, student.id));
  const [groupId, setGroupId] = useState(student.groupId ?? '');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const enrollmentId = activeYear
    ? history.data?.enrollments.find((item) => item.schoolYearId === activeYear.id)?.id
    : undefined;
  const options = (groups.data?.items ?? []).filter(
    (group) => !activeYear || group.schoolYearId === activeYear.id,
  );
  return (
    <form
      className="space-y-4"
      onSubmit={(event) => {
        event.preventDefault();
        if (!activeYear || !groupId) {
          setError(t('student.enroll.noYear'));
          return;
        }
        setBusy(true);
        void (
          enrollmentId
            ? peopleApi(api).updateEnrollment(student.id, enrollmentId, { groupId })
            : peopleApi(api).enroll(student.id, { schoolYearId: activeYear.id, groupId })
        )
          .then(onDone)
          .catch((thrown: unknown) => setError(describeError(t, thrown)))
          .finally(() => setBusy(false));
      }}
    >
      {error ? (
        <p role="alert" className="text-sm text-danger-700">
          {error}
        </p>
      ) : null}
      <Select
        label={t('student.enroll.group')}
        help={
          activeYear
            ? t('student.enroll.help', { year: activeYear.name })
            : t('student.enroll.noYear')
        }
        value={groupId}
        onChange={(event) => setGroupId(event.target.value)}
        options={[
          { value: '', label: t('assignments.pick') },
          ...options.map((group) => ({ value: group.id, label: group.name })),
        ]}
      />
      <div className="flex justify-end">
        <Button type="submit" loading={busy}>
          {t('common.save')}
        </Button>
      </div>
    </form>
  );
};

const SensitiveSection = ({
  studentId,
  guardians,
}: {
  studentId: string;
  guardians: GuardianItem[];
}) => {
  const t = useT();
  const api = useApi();
  const queries = useQueryClient();
  const sensitive = useQuery(peopleQueries.sensitive(api, studentId, true));
  const [editing, setEditing] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const refresh = () => queries.invalidateQueries({ queryKey: ['people', 'students', studentId] });
  const needsConsent = sensitive.isError && /consent/i.test(sensitive.error.message);
  return (
    <Card
      title={t('sensitive.title')}
      actions={
        sensitive.isSuccess ? (
          <Button size="sm" variant="secondary" onClick={() => setEditing(true)}>
            {t('sensitive.edit')}
          </Button>
        ) : undefined
      }
    >
      <p className="mb-3 text-xs text-gray-500">{t('sensitive.notice')}</p>
      {sensitive.isPending ? (
        <p className="text-sm text-gray-500">{t('common.loading')}</p>
      ) : sensitive.isError ? (
        <div className="space-y-3">
          <p role="status" className="text-sm text-warning-700">
            {needsConsent ? t('sensitive.noConsent') : describeError(t, sensitive.error)}
          </p>
          {needsConsent ? (
            <Button size="sm" onClick={() => setCapturing(true)}>
              {t('consent.capture')}
            </Button>
          ) : null}
        </div>
      ) : (
        <dl className="grid gap-3 text-sm sm:grid-cols-2">
          {(['eps', 'allergies', 'disabilities', 'religion', 'ethnicity'] as const).map((key) => (
            <div key={key}>
              <dt className="text-gray-500">{t(`sensitive.field.${key}`)}</dt>
              <dd className="mt-0.5 font-medium text-gray-900">
                {sensitive.data[key] || t('common.none')}
              </dd>
            </div>
          ))}
        </dl>
      )}
      <Modal open={editing} onClose={() => setEditing(false)} title={t('sensitive.edit')}>
        {editing && sensitive.data ? (
          <SensitiveForm
            studentId={studentId}
            current={sensitive.data}
            onDone={() => {
              setEditing(false);
              void refresh();
            }}
          />
        ) : null}
      </Modal>
      <Modal open={capturing} onClose={() => setCapturing(false)} title={t('consent.capture')}>
        {capturing ? (
          <ConsentForm
            studentId={studentId}
            guardians={guardians}
            onDone={() => {
              setCapturing(false);
              void refresh();
            }}
          />
        ) : null}
      </Modal>
    </Card>
  );
};

const HistoryCard = ({ studentId }: { studentId: string }) => {
  const t = useT();
  const api = useApi();
  const history = useQuery(peopleQueries.history(api, studentId));
  return (
    <Card title={t('student.history')}>
      {history.isPending ? (
        <p className="text-sm text-gray-500">{t('common.loading')}</p>
      ) : history.isError ? (
        <p className="text-sm text-gray-500">{describeError(t, history.error)}</p>
      ) : (
        <div className="grid gap-4 text-sm sm:grid-cols-2">
          <div>
            <p className="font-medium text-gray-900">{t('student.history.enrollments')}</p>
            <ul className="mt-1 space-y-1 text-gray-600">
              {history.data.enrollments.map((item) => (
                <li key={item.id}>
                  {item.schoolYearName} · {item.groupName} ·{' '}
                  {t(`student.enrollment.status.${item.status}` as never)}
                </li>
              ))}
              {history.data.enrollments.length === 0 ? <li>{t('common.none')}</li> : null}
            </ul>
          </div>
          <div>
            <p className="font-medium text-gray-900">{t('student.history.status')}</p>
            <ul className="mt-1 space-y-1 text-gray-600">
              {history.data.statusEvents.map((event) => (
                <li key={event.createdAt}>
                  {formatDate(event.effectiveOn)} · {t(`student.status.${event.status}` as never)}
                  {event.reason ? ` · ${event.reason}` : ''}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </Card>
  );
};

/**
 * The invitation state of a person, with the button to send or resend it.
 */
export const InvitationStatus = ({ userId, email }: { userId: string; email: string | null }) => {
  const t = useT();
  const api = useApi();
  const toast = useToast();
  const queries = useQueryClient();
  const invitations = useQuery(peopleQueries.invitations(api, userId));
  const latest: InvitationView | undefined = invitations.data?.items[0];
  const send = useMutation({
    mutationFn: () =>
      latest && latest.status !== 'accepted'
        ? peopleApi(api).resendInvitation(latest.id)
        : peopleApi(api).invite(userId),
    onSuccess: (invitation) => {
      void queries.invalidateQueries({ queryKey: ['people', 'invitations', userId] });
      toast.show({
        tone: invitation.lastEmailStatus === 'sent' ? 'success' : 'info',
        title: t('invite.sent'),
        description: invitation.lastEmailStatus ?? '',
      });
    },
    onError: (error) =>
      toast.show({
        tone: 'error',
        title: t('invite.action'),
        description: describeError(t, error),
      }),
  });
  if (!email) return <p className="text-xs text-gray-400">{t('invite.noEmail')}</p>;
  return (
    <p className="mt-1 flex items-center gap-2 text-xs text-gray-500">
      {latest ? (
        <Badge
          tone={
            latest.status === 'accepted' ? 'success' : latest.status === 'pending' ? 'info' : 'gray'
          }
        >
          {t(`invite.status.${latest.status}`)}
        </Badge>
      ) : (
        <span>{t('invite.status.none')}</span>
      )}
      {!latest || latest.status !== 'accepted' ? (
        <button
          type="button"
          className="control-touch inline-flex items-center rounded-control px-1 text-brand-600 underline underline-offset-2 hover:decoration-2"
          onClick={() => send.mutate()}
          disabled={send.isPending}
        >
          {latest ? t('invite.resend') : t('invite.action')}
        </button>
      ) : null}
    </p>
  );
};

const StudentAccount = ({ studentId }: { studentId: string }) => {
  const t = useT();
  const api = useApi();
  const toast = useToast();
  const create = useMutation({
    mutationFn: () => peopleApi(api).createAccount(studentId),
    onSuccess: (result) =>
      toast.show({
        tone: 'success',
        title: t('student.account.created'),
        description: result.email,
      }),
    onError: (error) =>
      toast.show({
        tone: 'error',
        title: t('student.account'),
        description: describeError(t, error),
      }),
  });
  return (
    <div className="mt-4 border-t border-gray-100 pt-3">
      <Button size="sm" variant="ghost" onClick={() => create.mutate()} disabled={create.isPending}>
        {t('student.account')}
      </Button>
      <p className="mt-1 text-xs text-gray-500">{t('student.account.help')}</p>
    </div>
  );
};
